import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "card" };
const _hoisted_2 = {
    key: 0,
    class: "px-3 lg:px-4 pt-3 pb-2"
};
const _hoisted_3 = { class: "flex justify-between items-center" };
const _hoisted_4 = { class: "font-semibold" };
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = { class: "my-1 text-muted collapsible-content" };
const _hoisted_7 = { class: "w-full relative aspect-4/3 sm:aspect-video lg:aspect-2/1" };
const _hoisted_8 = {
    key: 0,
    class: "absolute inset-0 flex justify-center items-center text-muted z-2"
};
import { ref, watch } from 'vue';
import { ___, toFixedNumberStr } from '@ts/common/language';
import { useDeferredChart } from '@ts/common/useDeferredChart';
import SvgVue from '@ts/components/utils/SvgVue.vue';
import Chart from 'chart.js/auto';
import {} from 'chart.js/auto';
import {} from '@ts/common/chart';
import annotationPlugin from 'chartjs-plugin-annotation';
import {} from '@ts/common/datasets';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChartLactate',
    props: {
        data: { default: undefined },
        help: { type: Boolean, default: false },
        hint: { type: Boolean, default: false },
        header: { type: Boolean, default: true },
        tooltip: { type: Boolean, default: true },
        overrideAnnotations: { type: Boolean, default: false },
        annotations: { default: [] }
    },
    setup(__props) {
        Chart.register(annotationPlugin);
        const props = __props;
        const showHelp = ref(false);
        const showPlaceholder = ref(true);
        const chart = ref(null);
        const containerRef = ref(null);
        let chartObj = undefined;
        // Create the chart only once its container has a real (non-zero) size — see useDeferredChart.
        useDeferredChart(containerRef, {
            create: () => {
                if (props.data !== undefined)
                    drawChart(props.data);
            },
            getChart: () => chartObj,
        });
        watch(() => props.data, (newValue, oldValue) => {
            if (newValue !== undefined)
                drawChart(newValue);
        }, { deep: true });
        function drawChart(data) {
            if (chart.value === null)
                return;
            const chartData = {
                datasets: [
                    {
                        data: data.chart_o2_lact.vo2.map((value, index) => {
                            return { x: data.chart_o2_lact.power[index], y: value };
                        }),
                        label: ___({
                            en: 'Oxygen uptake',
                            de: 'Sauerstoffaufnahme',
                        }),
                        unit: ___({
                            en: 'ml/min',
                            de: 'ml/min',
                        }),
                        tooltipDigits: 0,
                        borderColor: '#2fb8c2',
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y',
                    },
                    {
                        data: data.chart_o2_lact.vlaoxmax.map((value, index) => {
                            return { x: data.chart_o2_lact.power[index], y: value };
                        }),
                        label: ___({
                            en: 'Fuel requirement lacate',
                            de: 'Brennstoffbedarf Laktat',
                        }),
                        unit: ___({
                            en: 'mmol/l/min',
                            de: 'mmol/l/min',
                        }),
                        tooltipDigits: 2,
                        borderColor: '#cdb663',
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y1',
                    },
                    {
                        data: data.chart_o2_lact.lac_prod.map((value, index) => {
                            return { x: data.chart_o2_lact.power[index], y: value };
                        }),
                        label: ___({
                            en: 'Lactate production',
                            de: 'Laktatproduktion',
                        }),
                        unit: ___({
                            en: 'mmol/l/min',
                            de: 'mmol/l/min',
                        }),
                        tooltipDigits: 2,
                        borderColor: '#636362',
                        pointRadius: 0,
                        showLine: true,
                        fill: false,
                        yAxisID: 'y1',
                    },
                ],
            };
            const chartOptions = {
                normalized: true,
                parsing: false,
                maintainAspectRatio: false,
                responsive: true,
                animation: false,
                events: props.tooltip ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] : [],
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: ___({
                                en: 'Power [W]',
                                de: 'Leistung [W]',
                            }),
                        },
                        type: 'linear',
                        max: data.chart_o2_lact.power[data.chart_o2_lact.power.length - 1],
                        min: data.chart_o2_lact.power[0],
                        ticks: {
                            autoSkip: true,
                        },
                    },
                    y: {
                        type: 'linear',
                        position: 'left',
                        title: {
                            display: true,
                            text: 'VO2 [ml/min]',
                        },
                        max: Math.ceil(data.chart_o2_lact.vo2[data.chart_o2_lact.vo2.length - 1] / 100) * 100,
                        min: Math.floor(data.chart_o2_lact.vo2[0] / 100) * 100,
                        ticks: {
                            autoSkip: true,
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                    y1: {
                        type: 'linear',
                        position: 'right',
                        title: {
                            display: true,
                            text: ___({
                                en: 'Lactate kinetics [mmol/l/min]',
                                de: 'Laktatkinetik [mmol/l/min]',
                            }),
                        },
                        max: 5,
                        min: 0,
                        ticks: {
                            autoSkip: true,
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false,
                },
                plugins: {
                    ...(props.tooltip
                        ? {
                            tooltip: {
                                callbacks: {
                                    title: (context) => {
                                        return ___({
                                            en: 'At ' + context[0].label + ' W:',
                                            de: 'Bei ' + context[0].label + '  W:',
                                        });
                                    },
                                    label: (context) => {
                                        return context.dataset.label + ': ' + toFixedNumberStr(context.raw.y, context.dataset.tooltipDigits) + ' ' + context.dataset.unit;
                                    },
                                    labelColor: (context) => {
                                        return {
                                            borderColor: 'rgba(0, 0, 0, 0)',
                                            backgroundColor: context.dataset.borderColor ?? 'rgba(0, 0, 0, 0)',
                                            borderWidth: 0,
                                        };
                                    },
                                },
                            },
                        }
                        : {
                            tooltip: {
                                enabled: false,
                            },
                        }),
                    annotation: {
                        common: {
                            drawTime: 'afterDatasetsDraw',
                        },
                        annotations: [
                            ...(props.overrideAnnotations
                                ? []
                                : [
                                    {
                                        id: 'hline',
                                        type: 'line',
                                        mode: 'vertical',
                                        scaleID: 'x',
                                        value: data.threshold_abs,
                                        borderColor: '#FF6384',
                                        borderDash: [10, 5],
                                        borderDashOffset: 5,
                                        borderWidth: 2,
                                        label: {
                                            position: 'end',
                                            backgroundColor: '#FF6384',
                                            content: ___({
                                                en: 'Anaerobic Threshold',
                                                de: 'Anaerobe Schwelle',
                                            }),
                                            display: true,
                                        },
                                    },
                                ]),
                            ...props.annotations,
                        ],
                    },
                },
            };
            if (chartObj === undefined) {
                chartObj = new Chart(chart.value, {
                    type: 'line',
                    data: chartData,
                    options: chartOptions,
                });
                showPlaceholder.value = false;
            }
            else {
                chartObj.data = chartData;
                chartObj.options = chartOptions;
                chartObj.update();
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (__props.header)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(___)({
                                en: 'Metabolic Model',
                                de: 'Stoffwechselmodell',
                            })), 1 /* TEXT */),
                            (__props.help)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    onClick: _cache[1] || (_cache[1] = ($event) => (showHelp.value = !showHelp.value)),
                                    type: "button",
                                    class: "btn text-gray-350 dark:text-gray-450 hover:text-primary-500",
                                    "aria-label": _unref(___)({ en: 'Toggle help', de: 'Hilfe ein- und ausblenden' }),
                                    title: _unref(___)({ en: 'Help', de: 'Hilfe' })
                                }, [
                                    _cache[0] || (_setBlockTracking(-1, true),
                                        (_cache[0] = _createVNode(SvgVue, {
                                            class: "w-4 h-4",
                                            icon: "lucide/circle-question-mark"
                                        })).cacheIndex = 0,
                                        _setBlockTracking(1),
                                        _cache[0])
                                ], 8 /* PROPS */, _hoisted_5))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (__props.help)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["collapsible", showHelp.value ? 'expanded' : ''])
                            }, [
                                _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(___)({
                                    en: 'This graph represents the summary of the physiological variables. The intersection of lactate build-up and lactate breakdown gives the power at the anaerobic threshold.',
                                    de: 'Dies Grafik stellt die Zusammenfassung der physiologischen Größen dar. Der Schnittpunkt aus Laktataufbau und Laktatabbau ergibt die Leistung an der anaeroben Schwelle.',
                                })), 1 /* TEXT */)
                            ], 2 /* CLASS */))
                            : _createCommentVNode("v-if", true)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", {
                        class: "absolute inset-0",
                        ref_key: "containerRef",
                        ref: containerRef
                    }, [
                        _createElementVNode("canvas", {
                            class: "w-full h-full",
                            ref_key: "chart",
                            ref: chart
                        }, null, 512 /* NEED_PATCH */)
                    ], 512 /* NEED_PATCH */),
                    (showPlaceholder.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createElementVNode("div", null, [
                                _cache[2] || (_setBlockTracking(-1, true),
                                    (_cache[2] = _createVNode(SvgVue, {
                                        class: "w-4 h-4",
                                        icon: "lucide/chart-line"
                                    })).cacheIndex = 2,
                                    _setBlockTracking(1),
                                    _cache[2]),
                                _createTextVNode(" " + _toDisplayString(_unref(___)({ en: 'loading...', de: 'lädt...' })), 1 /* TEXT */)
                            ])
                        ]))
                        : _createCommentVNode("v-if", true)
                ])
            ]));
        };
    }
});
